// Mirrors AdaptiveCatalog.Api.Shared.Models.V_7._0.Products.BulkProductPromo*.
//
// MSP promos only. A vendor promo is a promotional COST that arrives with the vendor's data through the
// import / real-time extensions, so it is never authored in the admin UI.

// --- The two shapes a promotion's pricing can take ---
//
// The first two REPLACE the storefront's own markup, so they set the effective markup or margin and
// price from cost exactly as the storefront's normal markup does. The third takes a percentage OFF the
// price the shopper would otherwise pay, so it prices from the calculated price instead.
//
// Kept in step with ProductPromo.MARKUP_TYPE_* on the API, which is what actually prices the product.

/** Percent markup on cost, replacing the storefront's. */
export const PROMO_MARKUP_PERCENT = 'PERCENT';
/** Gross margin, replacing the storefront's. */
export const PROMO_MARKUP_GROSS_MARGIN = 'GROSS_MARGIN';
/** A positive percentage off the calculated price. Never a negative markup — see describePromoPricing. */
export const PROMO_MARKUP_DISCOUNT = 'DISCOUNT_PERCENT';

export const isDiscountPromo = (markupType?: string): boolean => markupType === PROMO_MARKUP_DISCOUNT;

/**
 * How a promotion's pricing reads in a list: "15% off", "20% margin", "10% markup".
 *
 * Says which of the two shapes it is, because the number alone is ambiguous — 20 as a margin and 20 as
 * a discount are different prices, and the column that just printed "20%" was the reason the two got
 * confused in the first place.
 */
export const describePromoPricing = (markupType?: string, markup?: number | null): string => {
    if (markup === undefined || markup === null) return '—';
    if (isDiscountPromo(markupType)) return `${markup}% off`;
    if (markupType === PROMO_MARKUP_GROSS_MARGIN) return `${markup}% margin`;
    return `${markup}% markup`;
};

/**
 * A promo entry as stored on a product. Mirrors the API's V_4.0 ProductPromo.
 *
 * Holds BOTH kinds now. An MSP entry is a link to a Promotion plus only what this product overrides,
 * so most of its fields are absent and mean "inherit" — read them through the campaign, never on their
 * own. A vendor entry is self-contained: a distributor's promotional cost, arriving with the vendor's
 * data through the extensions and never authored here.
 */
export interface IProductPromo {
    id: string;
    /** 'msp' or 'vendor'. Absent is treated as 'msp'. */
    type?: string;
    /** MSP entries: the campaign this inherits from. */
    promoId?: string;
    /** Vendor entries: the distributor the promotion came from. */
    vendor?: string;
    /** Vendor entries: the promotional unit cost. */
    cost?: number;
    name?: string;
    /** Empty or absent applies to every storefront. */
    storefronts?: string[];
    markupType?: string;
    markup?: number;
    /** UTC ISO. Absent starts immediately. */
    startsAt?: string;
    /** UTC ISO, exclusive. Absent never expires. */
    endsAt?: string;
    /** Units offered at the promo price. Absent is unlimited. */
    allocationQuantity?: number;
    allocationSold?: number;
}

/**
 * A promotion campaign. Mirrors the API's V_7.0 Promotion.
 *
 * This is the source of truth for the values its products inherit — nothing is copied onto them — so
 * editing one of these takes effect everywhere immediately.
 */
export interface IPromotion {
    id?: string;
    name?: string;
    description?: string;
    catalog?: string;
    /** Empty or absent applies to every storefront. */
    storefronts?: string[];
    markupType?: string;
    markup?: number;
    /** UTC ISO. Absent starts immediately. */
    startsAt?: string;
    /** UTC ISO, exclusive. Absent never expires. */
    endsAt?: string;
    /** Units offered at the promo price, PER PRODUCT — not shared across the campaign. */
    allocationQuantity?: number;
    /** Turns the campaign off without deleting it or unpicking its products. */
    active?: boolean;
    productCount?: number;
    lastUpdated?: string;
    updatedBy?: string;
}

export interface IBulkProductPromoRequest {
    productIds: string[];
    /** Badge label shown to the shopper, e.g. "Spring Clearance". */
    name?: string;
    /** Storefronts the promo applies to. Empty applies to every storefront. */
    storefronts?: string[];
    /**
     * What the promo charges, read according to markupType: the effective markup or margin for
     * PERCENT / GROSS_MARGIN, or a positive percentage off the calculated price for DISCOUNT_PERCENT.
     */
    markup?: number;
    markupType?: string;
    /** UTC ISO. Omitted starts immediately. */
    startsAt?: string;
    /** UTC ISO, exclusive. Omitted never expires. */
    endsAt?: string;
    /** Units offered at the promo price, per product. Omitted is unlimited. */
    allocationQuantity?: number;
    /** Replace any existing MSP promo on the product rather than adding alongside it. */
    replaceExisting?: boolean;
}

export interface IBulkProductPromoSkip {
    id: string;
    reason: string;
}

export interface IBulkProductPromoResponse {
    success: boolean;
    message?: string;
    updated: number;
    promoId?: string;
    skipped: IBulkProductPromoSkip[];
}

/** Products to add to, or remove from, an existing promotion. */
export interface IPromotionProductsRequest {
    productIds: string[];
}

export interface IPromotionProductSkip {
    id: string;
    reason: string;
}

export interface IPromotionProductsResponse {
    success: boolean;
    message?: string;
    /** Products actually changed. Excludes ones already in the requested state. */
    updated: number;
    /** The campaign's product count after the change. */
    productCount: number;
    skipped: IPromotionProductSkip[];
}
